// CHRONOLOGIE des échanges avec UN personnage (centre de messagerie) — CCT-97.
//
// Le centre de messagerie raisonne par PERSONNAGE : on fusionne ici tous ses fils (blocage,
// refus, validation — toutes missions) triés par horodatage, et on sépare visuellement les
// missions. L'UI reprend celle du chat scénarisé (avatars, bulles, horodatage) pour rester
// cohérente avec le reste de l'app.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Ionicons from 'react-native-vector-icons/Ionicons';

import ActionLoader from '../components/ActionLoader';
import ChatThread, { type ThreadMessage } from '../components/ChatThread';
import { StatusPill } from '../components/UsDetail';

import {
  getCharacterTimeline, replyUsChat, listAllUserStories,
  type CharacterTimeline, type CharacterMessage, type BoardUserStory,
} from '../api/userStories';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { useChatState } from '../context/ChatStateContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { getThemeColors } from '../utils/themeStyles';
import { scriptedHistoryFor } from '../utils/scriptedHistory';
import { getStoryChatState, type PersistedChatMsg } from '../api/storyChat';
import { TYPE_COLOR } from '../utils/cyb';
import { useT } from '../i18n/useT';
import { elementKey } from '../utils/elementId';
import { useUpdates } from '../context/UpdatesContext';
import type { MessageKey } from '../i18n';

const ACCENT = TYPE_COLOR.chat;
/** Ce que raconte un fil : sans ce repère, trois conversations sur la même tâche se lisent
 *  comme une seule, avec des relances qui semblent se répéter. */
// ⚠️ KEYS, not translated strings — a module-level map is built before any hook has run.
const REASON_LABEL: Record<string, MessageKey> = {
  blocked: 'charChat.tone.blocked',
  refused: 'charChat.tone.refused',
  validated: 'charChat.tone.validated',
};

// CCT-253 — plus d'avatar de repli BUNDLÉ : le joueur parle en son nom, et à défaut de
// photo de profil l'initiale colorée vaut mieux qu'un visage emprunté à une AUTRE histoire.
const PLAYER_AVATAR_FALLBACK = undefined;

/** Un message de la chronologie, enrichi du locuteur réel (chats à plusieurs personnages). */
type TimelineMsg = CharacterMessage & { speaker?: string };

const hhmm = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const dayLabel = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

export default function CharacterChatScreen({ navigation, route }: any) {
  const t = useT();
  const { getAccessToken } = useAuth();
  // Reading a conversation is what clears the message badge (CCT-350): seeing the
  // list is not reading it, and a notification opens straight here.
  const { markSeen } = useUpdates();
  useEffect(() => { markSeen('chat'); }, [markSeen]);
  const { isDarkTheme } = useTheme();
  const { playerAvatar } = usePlayer();
  const chatStateStore = useChatState();
  const { getCompletedItems } = useStoryProgress();
  const colors = getThemeColors(isDarkTheme);
  const characterId: number = Number(route?.params?.characterId);
  const missionFilter: number | null = route?.params?.missionId ?? null;
  const playerAvatarImg = playerAvatar ?? PLAYER_AVATAR_FALLBACK;

  const [data, setData] = useState<CharacterTimeline | null>(null);
  // Fils scénarisés PERSISTÉS (base) des missions concernées : sans eux, les répliques du
  // joueur manquaient dans la chronologie — l'état client ne survit pas au rechargement.
  const [stored, setStored] = useState<Record<number, Record<string, PersistedChatMsg[]>>>({});
  // Volet de sélection d'US (CCT-99) : la question posée au personnage porte sur UNE tâche
  // précise ; les options offertes changent selon qu'un context est choisi ou non.
  const [usList, setUsList] = useState<BoardUserStory[]>([]);
  const [contextUs, setContextUs] = useState<BoardUserStory | null>(null);
  const [busy, setBusy] = useState(true);
  // Réplique envoyée mais pas encore confirmée par le serveur : on l'affiche immédiatement
  // pour que la bulle « … » du personnage se lise SOUS la question, à la place de sa réponse.
  const [pendingChoice, setPendingChoice] = useState<string | null>(null);
  const [error, setError] = useState('');
  const scrollRef = useRef<ScrollView>(null);

  const load = useCallback(async () => {
    try { setData(await getCharacterTimeline(getAccessToken(), characterId, missionFilter)); }
    catch (e: any) { setError('Conversation indisponible.'); }
    finally { setBusy(false); }
  }, [characterId, missionFilter]);

  useEffect(() => { void load(); }, [load]);

  // Les US sont chargées DÈS l'ouverture du chat (plus seulement à l'ouverture du volet) :
  // le context est affiché par défaut, il doit donc être connu avant tout clic.
  useEffect(() => {
    void (async () => {
      try { setUsList((await listAllUserStories(getAccessToken())).userStories ?? []); }
      catch { setUsList([]); }
    })();
  }, [getAccessToken]);

  // Le volet ne propose que les US de la mission DÉBLOQUÉE dont on parle : proposer les
  // tâches d'une autre mission n'a aucun sens dans une conversation située.
  const missionOfChat = missionFilter ?? (data?.missions?.[0]?.missionId ?? null);
  const scoped = missionOfChat == null
    ? usList
    : usList.filter(u => u.mission_id === missionOfChat);
  // ⚠️ ONLY THE TASKS ALREADY HANDED OVER — CCT-351. The panel listed the mission's WHOLE
  // backlog: tasks the player has not unlocked yet, about which the character cannot say a word
  // without giving away what comes next. `BACKLOG` = not assigned yet.
  const ouvertes = scoped.filter(u => u.status !== 'BACKLOG');
  // An empty panel beats a lying one: with nothing handed over yet, there is simply nothing to
  // ask about.
  const contextChoices = ouvertes;

  // Sélection par défaut = la DERNIÈRE US débloquée de la mission (la plus avancée dans la
  // séquence) : c'est celle sur laquelle le joueur travaille quand il ouvre la discussion.
  useEffect(() => {
    if (contextUs || !contextChoices.length) return;
    setContextUs(contextChoices[contextChoices.length - 1]);
  }, [contextChoices, contextUs]);

  useEffect(() => {
    const missions = data?.missions ?? [];
    if (!missions.length) return;
    void (async () => {
      const entries = await Promise.all(missions.map(async (m) => {
        try { return [m.missionId, (await getStoryChatState(getAccessToken(), m.missionId)).chats ?? {}] as const; }
        catch { return [m.missionId, {}] as const; }
      }));
      setStored(Object.fromEntries(entries));
    })();
  }, [data]);

  // La chronologie commence par ce qui a été échangé DANS la mission (chats du scénario) : sans
  // ça, on n'affichait que les fils du moteur et la conversation semblait amputée (CCT-97).
  const scripted: TimelineMsg[] = (() => {
    if (!data) return [];
    const missions = missionFilter != null
      ? data.missions.filter(m => m.missionId === missionFilter)
      : data.missions;
    return missions.flatMap(m =>
      scriptedHistoryFor(m.missionId, data.characterName, {
        completed: getCompletedItems(m.missionId),
        getState: chatStateStore.getState,
        persisted: stored[m.missionId],
      }).map(s => ({
        role: s.role, text: s.text, at: '', speaker: s.speaker,
        missionId: m.missionId, missionTitle: m.title,
        elementId: s.itemId, reason: 'blocked' as const,
        chatId: -1, replies: [], closed: true,
      })));
  })();

  const msgs = [...scripted, ...(data?.messages ?? [])];

  // FILTER BY USER STORY — CCT-350.
  //
  // A character's timeline merges everything they ever said to the player: several tasks, several
  // missions, several threads per task. Read end to end, one loses track of which delivery a
  // remark was about.
  //
  // ⚠️ THE IDENTIFIER IS THE ONE THE PLAYER READS (`us_key`, e.g. `SIM-100-1`), the very one the
  // character says in their messages ("la user story SIM-100-1"). A filter labelled otherwise
  // would name the same task twice, in two vocabularies.
  //
  // ⚠️ TWO FORMS OF ONE ID, ONCE AGAIN: the timeline carries `el-107600` (the app's form) and the
  // task list `107600` (the database's). Compared raw they never match — and a filter that never
  // matches simply empties the screen.
  const [usFilter, setUsFilter] = useState<string | null>(null);
  const usOf = (elementId: any) =>
    usList.find(u => String(u.element_id) === elementKey(elementId)) ?? null;
  // Only the tasks PRESENT in the timeline: offering a filter that yields nothing is noise.
  const usPresent = (() => {
    const seen = new Map<string, BoardUserStory>();
    for (const m of msgs) {
      const u = usOf(m.elementId);
      if (u) seen.set(String(u.element_id), u);
    }
    return [...seen.values()];
  })();
  const visibles = usFilter ? msgs.filter(m => elementKey(m.elementId) === usFilter) : msgs;
  // Séparateurs calculés ICI : le rendu est générique, c'est l'écran qui sait ce qui change
  // (mission, jour) d'un message à l'autre.
  const thread: ThreadMessage[] = visibles.map((m: TimelineMsg, i: number) => {
    const prev = i > 0 ? visibles[i - 1] : null;
    const newMission = !prev || prev.missionId !== m.missionId;
    const newDay = !prev || dayLabel(prev.at) !== dayLabel(m.at);
    // Un personnage a PLUSIEURS fils sur la même US (coup de pouce, retour de livraison,
    // validation). Fusionnés sans repère, ils donnent l'illusion de questions répétées : la
    // même relance revient, venue d'un autre fil. On marque donc le changement de fil.
    const newThread = !prev || prev.chatId !== m.chatId;
    const parts = [
      newMission ? m.missionTitle : null,
      newThread ? (REASON_LABEL[m.reason] ? t(REASON_LABEL[m.reason]) : null) : null,
      (newMission || newDay) ? dayLabel(m.at) : null,
    ].filter(Boolean);
    return {
      role: m.role, text: m.text, at: m.at || undefined, speaker: m.speaker,
      separator: (newMission || newDay || newThread) ? parts.join(' · ') : undefined,
    };
  });
  const engine = data?.messages ?? [];
  const last = engine.length ? engine[engine.length - 1] : null;
  // Seul le DERNIER message du personnage, sur un fil non clos, reste actionnable.
  const CONTEXT_OPTIONS = [
    { id: 'ctx_functional', label: t('charChat.ctx.functional') },
    { id: 'ctx_technical', label: t('charChat.ctx.technical') },
  ];
  const baseOptions = last && last.role === 'character' && !last.closed ? last.replies : [];
  // Une US sélectionnée REMPLACE les options du fil : on ne demande plus « avez-vous compris »,
  // on interroge sur la tâche désignée.
  const options = contextUs ? CONTEXT_OPTIONS : baseOptions;
  const avatar = data?.avatarkey && data.avatarkey.startsWith('http') ? data.avatarkey : null;
  const name = data?.characterName || t('chat.character');

  const answer = async (choice: string) => {
    if (!last) return;
    const label = options.find(o => o.id === choice)?.label ?? '';
    setPendingChoice(label);
    setBusy(true); setError('');
    try {
      await replyUsChat(getAccessToken(), Number(last.missionId), last.elementId, choice,
                        contextUs?.element_id);
      await load();
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 60);
    } catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setBusy(false); setPendingChoice(null); }
  };

  const Avatar = () => (
    avatar
      ? <Image source={{ uri: avatar }} style={st.msgAvatarImage} resizeMode="cover" />
      : <View style={[st.msgAvatar, { backgroundColor: ACCENT }]}>
          <Text style={st.msgAvatarText}>{name.slice(0, 1).toUpperCase()}</Text>
        </View>
  );

  return (
    <SafeAreaView style={[st.root, { backgroundColor: colors.background }]}>
      <View style={[st.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation?.goBack?.()} style={st.back}>
          <Ionicons name="arrow-back" size={22} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={st.headerAvatar}><Avatar /></View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={1} style={[st.title, { color: colors.textPrimary }]}>{name}</Text>
          <Text numberOfLines={1} style={[st.sub, { color: colors.textSecondary }]}>
            {t(msgs.length > 1 ? 'charChat.messageMany' : 'charChat.messageOne', { count: msgs.length })}
            {data?.missions?.length
              ? ` · ${t(data.missions.length > 1 ? 'charChat.missionMany' : 'charChat.missionOne', { count: data.missions.length })}`
              : ''}
          </Text>
        </View>
      </View>

      {/* CCT-354 — one chip per user story PRESENT in this conversation, named as the character
          names it, plus "All".
          ⚠️ SHOWN EVEN FOR A SINGLE TASK. The first version hid the bar below two chips, on the
          grounds that a lone chip filters nothing — and the player, who had asked for this filter,
          opened the screen and found NOTHING. Half of what a filter gives is telling you what the
          conversation is about; the other half is switching. One chip still gives the first. */}
      {usPresent.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={st.filterBar} contentContainerStyle={st.filterRow}>
          {[null, ...usPresent].map((u) => {
            const key = u ? String(u.element_id) : null;
            const on = usFilter === key;
            return (
              <TouchableOpacity key={key ?? 'all'} activeOpacity={0.75}
                onPress={() => setUsFilter(on ? null : key)}
                style={[st.chip, {
                  backgroundColor: on ? colors.primary : colors.surfaceSecondary,
                  borderColor: on ? colors.primary : colors.border,
                }]}>
                <Text numberOfLines={1}
                  style={[st.chipText, { color: on ? colors.badgeText : colors.textSecondary }]}>
                  {u ? (u.us_key || u.title || `US ${u.element_id}`) : t('chat.all')}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      {busy && !data ? (
        <View style={st.center}><ActionLoader label={t('charChat.loading')} /></View>
      ) : error && !data ? (
        <View style={st.center}>
          <Ionicons name="chatbubble-ellipses-outline" size={40} color={colors.textTertiary} />
          <Text style={[st.dim, { color: colors.textTertiary }]}>{error}</Text>
        </View>
      ) : (
        <>
          <ChatThread
            mode="character"
            scrollRef={scrollRef}
            context={{
              items: contextChoices,
              selected: contextUs,
              onSelect: setContextUs,
              onClear: () => setContextUs(null),
              keyOf: (u: BoardUserStory) => u.id,
              labelOf: (u: BoardUserStory) => u.title || `US ${u.element_id}`,
              subLabelOf: (u: BoardUserStory) => u.mission_title ?? `Mission ${u.mission_id}`,
              badgeOf: (u: BoardUserStory) => <StatusPill status={u.status} small />,
            }}
            messages={pendingChoice
              ? [...thread, { role: 'student' as const, text: pendingChoice }]
              : thread}
            characterName={name}
            characterAvatar={avatar}
            playerAvatar={playerAvatarImg}
            options={options}
            busy={busy}
            awaiting={busy && !!data}
            onChoose={answer}
            emptyLabel={t('charChat.empty')}
          />
          {!!error && <Text style={[st.dim, { color: colors.error }]}>{error}</Text>}
        </>
      )}
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  root: { flex: 1, minHeight: 0 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
  // Without `flexGrow: 0`, a ScrollView takes the parent's flex in a column layout: the filter
  // bar stretched over the whole height (the same trap as the board).
  filterBar: { flexGrow: 0, flexShrink: 0 },
  filterRow: { gap: 8, paddingHorizontal: 12, paddingVertical: 8, alignItems: 'center' },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6, maxWidth: 220 },
  chipText: { fontSize: 12, fontWeight: '600' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1 },
  back: { padding: 4 },
  headerAvatar: { width: 32, height: 32, borderRadius: 16, overflow: 'hidden' },
  title: { fontSize: 15, fontWeight: '700' },
  sub: { fontSize: 11 },
  scroll: { flex: 1, minHeight: 0 },
  content: { padding: 12 },
  msgAvatarImage: { width: 32, height: 32, borderRadius: 16 },
  msgAvatar: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  msgAvatarText: { fontSize: 11, fontWeight: 'bold', color: '#FFFFFF' },
  dim: { fontSize: 12, textAlign: 'center', marginTop: 8 },
});
